import { get } from 'lodash';
import { cleanPayload } from 'src/helpers/common';
import { DATE_FIELD_TYPES, formatDateDisplay } from 'src/helpers/dateFields';

/**
 * Value data structure with optional alt field
 */
export interface ValueData {
  data?: string;
  alt?: string;
}

/**
 * Parsed field with field info
 */
export interface ParsedFieldInfo {
  field?: {
    dataType?: string;
    fieldType?: string;
    dateDisplayStyle?: string;
  };
  fieldType?: string;
}

/**
 * Raw view field node from GraphQL response
 */
export interface RawViewFieldNode extends ParsedFieldInfo {
  value: string | null;
}

/**
 * Parsed view field with display value
 */
export interface ParsedViewField extends Omit<RawViewFieldNode, 'value'> {
  value: ValueData[] | null;
  show: string | null;
}

/**
 * Raw super view data from GraphQL response
 */
export interface RawSuperViewData {
  fieldType?: string;
  label?: string;
  slug?: string;
  value: string | null;
  fallbackMessage?: string | null;
  dateDisplayStyle?: string;
  isSystemField?: boolean | null;
}

/**
 * Parsed super view data with display value
 */
export interface ParsedSuperViewData extends Omit<RawSuperViewData, 'value'> {
  value: ValueData[] | null;
  show: string | null;
  fallbackMessage?: string | null;
}

/**
 * Super view response structure
 */
export interface SuperViewResponse {
  superView?: RawSuperViewData[];
  sharedSuperView?: RawSuperViewData[];
}

/**
 * Reserved slug for the in-list "augments" sentinel row. The backend appends
 * one entry with this slug to carry view-level enrichments (resolved title,
 * future thumbnail/duration/etc.) as a JSON-encoded object. The parser
 * strips this row before consumers see it; consumers that want the augments
 * call `getSuperViewAugments(payload, shared?)`.
 */
export const AKOMI_AUGMENTS_SLUG = '_akomi_augments';

/**
 * View-level enrichments carried in the augments sentinel row.
 * All keys optional; backend may add new ones over time with zero parser changes.
 */
export interface SuperViewAugments {
  title?: string;
}

/**
 * True when `entry` is the reserved augments sentinel row. Matches on slug
 * AND isSystemField=true so a legacy/imported FieldDef that happens to use
 * the reserved slug name cannot impersonate the sentinel.
 */
const isAugmentsSentinel = (entry: RawSuperViewData | undefined | null): boolean =>
  !!entry && entry.slug === AKOMI_AUGMENTS_SLUG && entry.isSystemField === true;

/**
 * Parse view field values from a GraphQL payload
 * @param payload - The raw GraphQL response payload
 * @returns Array of parsed view fields with display values
 */
export const parseViewFieldValues = (payload: Record<string, unknown>): ParsedViewField[] => {
  const data = cleanPayload(payload) as RawViewFieldNode[];
  return data.map((node: RawViewFieldNode): ParsedViewField => {
    try {
      const value = JSON.parse(node.value as string) as ValueData[];
      return {
        ...node,
        value,
        show: toDisplayType(node.field?.dataType, value, node.field?.fieldType, node.field?.dateDisplayStyle),
      };
    } catch (_error) {
      if (node.value !== null) {
        return { ...node, value: null, show: 'Error' };
        // This is so we won't show text "error" in the fields that are returned as null from backend
      } else {
        return { ...node, value: null, show: null };
      }
    }
  });
};

/**
 * Parse super view data from GraphQL response. Filters out the reserved
 * `_akomi_augments` sentinel row before parsing so consumers never see it as
 * a regular field. Augment data is accessed separately via `getSuperViewAugments`.
 * @param payload - The super view response payload
 * @param shared - Whether this is a shared super view
 * @returns Array of parsed super view data with display values
 */
export const parseSuperView = (
  payload: SuperViewResponse | null | undefined,
  shared: boolean = false,
): ParsedSuperViewData[] => {
  const key = shared ? 'sharedSuperView' : 'superView';

  return payload
    ? (payload[key]
        ?.filter((data: RawSuperViewData) => !isAugmentsSentinel(data))
        .map((data: RawSuperViewData): ParsedSuperViewData => {
          try {
            const value = JSON.parse(data.value as string) as ValueData[];
            return {
              ...data,
              value,
              show: toDisplayType(data.fieldType, value, data.fieldType, data.dateDisplayStyle),
            };
          } catch (_error) {
            if (data.value !== null) {
              return { ...data, value: null, show: 'Error' };
              // This is so we won't show text "error" in the fields that are returned as null from backend
            } else {
              return { ...data, value: null, show: null };
            }
          }
        }) ?? [])
    : [];
};

/**
 * Extract the view-level augments object (resolved title, etc.) from a
 * SuperView GraphQL response. Returns `{}` if no sentinel row is present or
 * its value can't be safely decoded into a plain object.
 *
 * The backend sends `value = json.dumps(augments)` per Akomi's JSONString
 * convention; Graphene then encodes that string again, so the wire payload
 * is double-encoded and requires `JSON.parse(JSON.parse(value))`.
 */
export const getSuperViewAugments = (
  payload: SuperViewResponse | null | undefined,
  shared: boolean = false,
): SuperViewAugments => {
  if (!payload) return {};
  const key = shared ? 'sharedSuperView' : 'superView';
  const sentinel = payload[key]?.find(isAugmentsSentinel);
  if (!sentinel || typeof sentinel.value !== 'string') return {};

  let parsed: unknown;
  try {
    parsed = JSON.parse(sentinel.value);
    // Akomi convention: JSONString resolvers `json.dumps` before return,
    // and Graphene serializes again. Unwrap the inner JSON string when present.
    if (typeof parsed === 'string') {
      parsed = JSON.parse(parsed);
    }
  } catch (_error) {
    return {};
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

  const out: SuperViewAugments = {};
  const obj = parsed as Record<string, unknown>;
  if (typeof obj.title === 'string' && obj.title.length > 0) {
    out.title = obj.title;
  }
  return out;
};

/**
 * Convert value data to display string based on display type
 * @param displayType - The type of display formatting to apply
 * @param value - The parsed value data array
 * @returns Formatted display string
 */
export const toDisplayType = (
  displayType: string | undefined,
  value: ValueData[] | null | undefined,
  fieldType?: string,
  dateDisplayStyle?: string,
): string => {
  if (fieldType && (DATE_FIELD_TYPES as readonly string[]).includes(fieldType)) {
    return (
      value
        ?.map((val: ValueData) => {
          const raw = get(val, 'data', '');
          return raw ? formatDateDisplay(raw, fieldType, dateDisplayStyle) : '';
        })
        .filter(Boolean)
        .join(', ') ?? ''
    );
  }

  // All other field types — show raw data value
  return value?.map((val: ValueData) => get(val, 'data', '')).join(', ') ?? '';
};
